import React, { useState } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { ApiSdk } from '@/httpclient';
import { LanguageModel, GuideOverseaExperience } from '@/__generated';
import { Form, Modal, Row, Input, Col, Divider, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ERROR_EXCEPTION } from '../../constants';
import GuideAttachment from './components/GuideAttachment';
import LanguageContent from './components/LanguageContent';
import SelfIntroduction from './components/SelfIntroduction';
import RatingContent from './components/RatingContent';
import './style.less';
import FullPageLoading from '@/components/FullPageLoading';
import GuideBasicInfo from './components/GuideBasicInfo';
import { checkValidGuideDataBeforeSave } from './checkGuideDataBeforeSave';
import { formatApiDate } from '@/config';

type Props = {
  languages: LanguageModel[];
  onClose: (reload?: boolean) => void;
};
const isNewForm = true;
export default function Create({ languages, onClose }: Props) {
  const { auth } = useAppContext();
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState('');
  const [previewCardFront, setPreviewCardFront] = useState('');
  const [previewCardBack, setPreviewCardBack] = useState('');
  const [previewResume, setPreviewResume] = useState('');
  const [previewCV, setPreviewCV] = useState('');
  const [selectedFilePhoto, setSelectedFilePhoto] = useState<File | null>(null);
  const [selectedFileCardFront, setSelectedFileCardFront] = useState<File | null>(null);
  const [selectedFileCardBack, setSelectedFileCardBack] = useState<File | null>(null);
  const [selectedFileResume, setSelectedFileResume] = useState<File | null>(null);
  const [selectedFileCV, setSelectedFileCV] = useState<File | null>(null);

  const onImageChange = async (e: { currentTarget: HTMLInputElement }) => {
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      setSelectedFilePhoto(e.currentTarget.files[0]);
      const objectUrl = URL.createObjectURL(e.currentTarget.files[0]);
      setPreviewPhoto(objectUrl);
    }
  };

  const onImageCardFront = async (e: { currentTarget: HTMLInputElement }) => {
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      setSelectedFileCardFront(e.currentTarget.files[0]);
      const objectUrl = URL.createObjectURL(e.currentTarget.files[0]);
      setPreviewCardFront(objectUrl);
    }
  };

  const onImageChangeCardBack = async (e: { currentTarget: HTMLInputElement }) => {
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      setSelectedFileCardBack(e.currentTarget.files[0]);
      const objectUrl = URL.createObjectURL(e.currentTarget.files[0]);
      setPreviewCardBack(objectUrl);
    }
  };

  const onImageChangeResume = async (e: { currentTarget: HTMLInputElement }) => {
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      setSelectedFileResume(e.currentTarget.files[0]);
      setPreviewResume(e.currentTarget.files[0].name);
    }
  };

  const onFileChangeCV = async (e: { currentTarget: HTMLInputElement }) => {
    if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
      setSelectedFileCV(e.currentTarget.files[0]);
      setPreviewCV(e.currentTarget.files[0].name);
    }
  };

  const onRemovePhoto = () => {
    setPreviewPhoto('');
    setSelectedFilePhoto(null);
  };
  const onRemoveCardFront = () => {
    setPreviewCardFront('');
    setSelectedFileCardFront(null);
  };
  const onRemoveCardBack = () => {
    setPreviewCardBack('');
    setSelectedFileCardBack(null);
  };
  const onRemoveResume = () => {
    setPreviewResume('');
    setSelectedFileResume(null);
  };
  const onRemoveCV = () => {
    setPreviewCV('');
    setSelectedFileCV(null);
  };

  const processUploadImage = async fileUpload => {
    const response = await ApiSdk.MediaService.uploadFile({ fileName: fileUpload?.name ?? '', formFile: fileUpload });
    if (response.success) {
      return response;
    } else {
      return {};
    }
  };
  const saveGuide = values => {
    ApiSdk.GuideService.register({ body: values })
      .then(res => {
        if (res.success) {
          onClose(true);
        } else {
          message.error(t(res.message || ''));
        }
      })
      .catch(() => {
        message.error(ERROR_EXCEPTION, 10);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onSubmit = async values => {
    if (!checkValidGuideDataBeforeSave(values, form)) {
      return;
    }
    setLoading(true);
    try {
      const dataSend = { ...values };
      if (dataSend.birthDate) {
        dataSend.birthDate = formatApiDate(dataSend.birthDate);
      }

      if (selectedFilePhoto && dataSend.profileImageId === undefined) {
        const imagePhoto = await processUploadImage(selectedFilePhoto);
        if (imagePhoto?.fileId) {
          dataSend.profileImage = imagePhoto;
          dataSend.profileImageId = imagePhoto.fileId;
        }
      }

      if (selectedFileCardFront && dataSend.businessCardFrontId === undefined) {
        const imageCardFront = await processUploadImage(selectedFileCardFront);
        if (imageCardFront?.fileId) {
          dataSend.businessCardFront = imageCardFront;
          dataSend.businessCardFrontId = imageCardFront.fileId;
        }
      }

      if (selectedFileCardBack && dataSend.businessCardBackId === undefined) {
        const imageCardBack = await processUploadImage(selectedFileCardBack);
        if (imageCardBack?.fileId) {
          dataSend.businessCardBack = imageCardBack;
          dataSend.businessCardBackId = imageCardBack.fileId;
        }
      }

      if (selectedFileResume && dataSend.resumeUploadId === undefined) {
        const fileResume = await processUploadImage(selectedFileResume);
        if (fileResume?.fileId) {
          dataSend.resumeUploadId = fileResume.fileId;
          dataSend.resumeUpload = fileResume;
        }
      }

      if (selectedFileCV && dataSend.cvUploadId === undefined) {
        const fileCV = await processUploadImage(selectedFileCV);
        if (fileCV?.fileId) {
          dataSend.cvUploadId = fileCV.fileId;
          dataSend.cvUpload = fileCV;
        }
      }

      dataSend.supportedTypes = values.supportedTypes ?? [];
      dataSend.supportedAreas = values.supportedAreas ?? [];
      dataSend.maxNumber = dataSend.maxNumber || 0;
      dataSend.overseaExperiences = (values.overseaExperiences as GuideOverseaExperience[])
        ?.filter(oversea => {
          return oversea.from || oversea.to || oversea.place || oversea.details;
        })
        .map(d => {
          return {
            ...d,
            from: d.from ? formatApiDate(d.from) : undefined,
            to: d.to ? formatApiDate(d.to) : undefined,
          };
        });
      dataSend.supportedLanguages = dataSend.supportedLanguages?.map(d => {
        return {
          ...d,
          obtainedDate: d.obtainedDate ? formatApiDate(d.obtainedDate) : undefined,
        };
      });
      saveGuide(dataSend);
    } catch (error) {
      setLoading(false);

      if ((error as any)?.message) {
        message.warning((error as any)?.response?.data?.message || ERROR_EXCEPTION, 10);
      } else {
        message.warning(ERROR_EXCEPTION, 10);
      }
    }
  };

  const onValuesChangeForm = values => {
    if (Object.keys(values)[0] === 'maxNumberExperience') {
      if (form.getFieldValue('maxNumber') === undefined || form.getFieldValue('maxNumber') === '') {
        form.setFieldsValue({ maxNumber: null });
      }
    }
  };
  const { formLayout, labelCol, wrapperCol } = React.useMemo(() => {
    if (window.innerWidth > 800) {
      return {
        formLayout: 'vertical',
        labelCol: undefined,
        wrapperCol: undefined,
      };
    }
    return {
      formLayout: 'horizontal',
      labelCol: { span: 10, sm: 10, xs: 10 },
      wrapperCol: { span: 14, sm: 14, xs: 14 },
    };
  }, []);
  return (
    <Modal
      className="nta-custom-form modal-create-guide-search responsive-modal create-guide-modal"
      visible={true}
      maskClosable={false}
      style={{ top: 10 }}
      width={900}
      title={isNewForm ? '新規登録' : t('staffSearch_edit')}
      forceRender={true}
      okText={isNewForm ? t('staffSearch_new') : t('staffSearch_save')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn' }}
      onCancel={() => {
        onClose(false);
      }}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            onSubmit(values);
          })
          .catch(err => {
            if (err?.errorFields.length) {
              const fieldsName = err.errorFields[0].name;
              form.scrollToField(fieldsName, { behavior: 'smooth' });
            }
          });
      }}
    >
      <FullPageLoading loading={loading} />
      <Form
        form={form}
        className="guide-info h-adr"
        layout={formLayout as any}
        onValuesChange={onValuesChangeForm}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        labelAlign={'left'}
        scrollToFirstError
        initialValues={{
          enabled: true,
          nationality: 'JP',
          isPersonalBusinessRegistration: false,
          hasInterpretationExperience: false,
          bankAccountType: 1,
          maxNumberExperience: false,
          maxNumberOfNights: 0,
          overseaExperiences: [
            {
              from: undefined,
              to: undefined,
              place: '',
              details: '',
            },
          ],
        }}
      >
        <Form.Item name="id" className="hidden">
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="userId" className="hidden">
          <Input type="hidden" />
        </Form.Item>

        <Row gutter={6} style={{ marginBottom: 5 }}>
          <Col md={10} xs={24}>
            <Row gutter={6}>
              <Col md={24} xs={24}>
                <h3>プロフィール</h3>
              </Col>
              <Col md={24} xs={24}>
                <GuideAttachment title={t('guideSerchphoto')} fileName={previewPhoto} onChange={onImageChange} onRemove={onRemovePhoto} />
              </Col>
              <Col md={24} xs={24}>
                <GuideAttachment
                  title={t('guideSerchbusinessCardFrontPhoto')}
                  fileName={previewCardFront}
                  onChange={onImageCardFront}
                  onRemove={onRemoveCardFront}
                />
              </Col>
              <Col md={24} xs={24}>
                <GuideAttachment
                  title={t('guideSerchbusinessCardBackPhoto')}
                  fileName={previewCardBack}
                  onChange={onImageChangeCardBack}
                  onRemove={onRemoveCardBack}
                />
              </Col>
            </Row>
          </Col>

          <GuideBasicInfo isCreateForm={true} />
        </Row>
        <Divider />
        <LanguageContent form={form} languages={languages} isMyProfile={false} />
        <Divider />
        <SelfIntroduction
          previewResume={previewResume}
          previewCV={previewCV}
          onRemoveResume={onRemoveResume}
          onImageChangeResume={onImageChangeResume}
          onRemoveCV={onRemoveCV}
          onFileChangeCV={onFileChangeCV}
          isNewForm={isNewForm}
          form={form}
          formVisible={true}
        />
        <Divider />
        <RatingContent auth={auth} isProfile={false} isNewForm={isNewForm} form={form} />
      </Form>
    </Modal>
  );
}
